import { useMemo, useState } from 'react';
import { useRigg } from '@/data/RiggProvider.tsx';
import { api } from '@/lib/api.ts';
import { Page, Row } from './Page.tsx';
import { GitHubMark } from '@/components/Pulses.tsx';
import { Button, Dialog, Empty, Field, Input, Select, Textarea, Tip, useToast } from '@/components/ui.tsx';
import type { Repo } from '@shared/domain.ts';

/**
 * Repositories.
 *
 * A project can have several, which is why the repository hangs off the task
 * and the routine rather than off the project. It is also the join between a
 * webhook and a task: an event arrives naming `acme/site`, and this table is
 * what turns that into "these three tasks care".
 */
export function ReposScreen() {
  const { data, project, isAdmin, config } = useRigg();
  const { notify } = useToast();
  const [adding, setAdding] = useState(false);
  const [fullName, setFullName] = useState('');
  const [branch, setBranch] = useState('');
  const [busy, setBusy] = useState(false);
  const [guarding, setGuarding] = useState<Repo | null>(null);
  const [globs, setGlobs] = useState('');

  const repos = useMemo(
    () => Object.values(data.repos).sort((a, b) => `${a.owner}/${a.name}`.localeCompare(`${b.owner}/${b.name}`)),
    [data.repos],
  );

  /** What the App can already see, so adding a repo is a pick rather than a typing exercise. */
  const suggestions = useMemo(() => {
    const taken = new Set(repos.map((r) => `${r.owner}/${r.name}`.toLowerCase()));
    return data.installationRepos
      .map((r) => `${r.owner}/${r.name}`)
      .filter((name) => !taken.has(name.toLowerCase()))
      .sort();
  }, [data.installationRepos, repos]);

  const installed = data.installationRepos.length > 0;

  const add = async () => {
    if (!project || !fullName.trim()) return;
    setBusy(true);
    try {
      await api.addRepo({
        project_id: project.id,
        full_name: fullName.trim(),
        default_branch: branch.trim() || undefined,
      });
      setAdding(false);
      setFullName('');
      setBranch('');
    } catch (error) {
      notify('Could not add the repository', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  const openGuard = (repo: Repo) => {
    setGuarding(repo);
    setGlobs((repo.protected_paths ?? []).join('\n'));
  };

  const saveGuard = async () => {
    if (!guarding) return;
    setBusy(true);
    try {
      await api.updateRepo(guarding.id, {
        protected_paths: globs.split('\n').map((line) => line.trim()).filter(Boolean),
      });
      setGuarding(null);
    } catch (error) {
      notify('Could not save the protected paths', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page
      title="Repositories"
      blurb="A project can have as many as it needs. Rigg reads their pull requests and check runs; it never pushes, merges, or runs CI."
      actions={
        isAdmin ? (
          <Button tone="primary" onClick={() => setAdding(true)}>
            Add a repository
          </Button>
        ) : null
      }
    >
      {repos.length === 0 ? (
        <Empty
          title="No repositories yet"
          hint="Add one so CI status and pull requests show up on your tasks."
        />
      ) : (
        <div>
          {repos.map((repo) => {
            const live = Boolean(repo.installation_id);
            return (
              <Row
                key={repo.id}
                title={
                  <span className="flex min-w-0 items-center gap-2">
                    <GitHubMark className="shrink-0 text-mute" />
                    <a
                      href={`https://github.com/${repo.owner}/${repo.name}`}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 truncate text-t1 no-underline hover:underline"
                    >
                      {repo.owner}/{repo.name}
                    </a>
                    {repo.private ? (
                      <span className="rounded-full bg-pill px-2 py-px text-[11px] font-semibold text-t7">
                        private
                      </span>
                    ) : null}
                  </span>
                }
                subtitle={
                  <span className="font-mono text-[11.5px]">
                    default branch {repo.default_branch}
                    {repo.protected_paths?.length ? (
                      <Tip content={`Auto-merge never waives approval for: ${repo.protected_paths.join(', ')}`}>
                        <span className="font-sans">
                          {' · '}
                          {repo.protected_paths.length} protected path
                          {repo.protected_paths.length === 1 ? '' : 's'}
                        </span>
                      </Tip>
                    ) : null}
                  </span>
                }
                meta={
                  <Tip
                    content={
                      live
                        ? 'The GitHub App is installed here — CI status arrives by webhook, within a second.'
                        : 'No App installation. CI status falls back to polling every couple of minutes, if a token is set.'
                    }
                  >
                    <span
                      className={live ? 'font-semibold text-accent' : 'font-semibold text-amberx'}
                    >
                      {live ? 'live via webhooks' : 'polled'}
                    </span>
                  </Tip>
                }
                actions={
                  isAdmin ? (
                    <span className="flex gap-2">
                      <Button size="sm" onClick={() => openGuard(repo)}>
                        Protected paths
                      </Button>
                      <Button
                        tone="danger"
                        size="sm"
                        onClick={async () => {
                          try {
                            await api.removeRepo(repo.id);
                          } catch (error) {
                            notify('Could not remove it', {
                              body: (error as Error).message,
                              tone: 'bad',
                            });
                          }
                        }}
                      >
                        Remove
                      </Button>
                    </span>
                  ) : null
                }
              />
            );
          })}
        </div>
      )}

      {!installed && config?.github.app_configured === false ? (
        <p className="mt-6 rounded-xl border border-amberx/25 bg-amberx/6 px-4 py-3 text-[13px] leading-relaxed text-t3">
          No GitHub App is configured for this deployment, so CI status has to be polled and comments
          need a personal access token. <a href="/settings">Set one up in Settings</a> — it takes two
          clicks and makes the build status live.
        </p>
      ) : null}

      <Dialog
        open={guarding !== null}
        onOpenChange={(open) => {
          if (!open) setGuarding(null);
        }}
        title={guarding ? `Protected paths in ${guarding.owner}/${guarding.name}` : 'Protected paths'}
        description="A task's auto-merge never waives approval for a pull request that changes one of these. It waits for a person to approve it instead; everything else still merges on its own."
        footer={
          <>
            <Button onClick={() => setGuarding(null)}>Cancel</Button>
            <Button tone="primary" disabled={busy} onClick={() => void saveGuard()}>
              Save
            </Button>
          </>
        }
      >
        <Field
          label="Path globs, one per line"
          hint="* matches within one folder, ** any depth — .github/** covers every workflow. Leave empty to protect nothing."
        >
          <Textarea
            value={globs}
            onChange={(event) => setGlobs(event.target.value)}
            placeholder={'.github/**\nscripts/ci/**'}
            rows={5}
            className="font-mono"
          />
        </Field>
      </Dialog>

      <Dialog
        open={adding}
        onOpenChange={setAdding}
        title="Add a repository"
        description="Rigg only reads. It needs to see the repository to report pull requests and check runs on it."
        footer={
          <>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button tone="primary" disabled={busy || !fullName.trim()} onClick={() => void add()}>
              Add
            </Button>
          </>
        }
      >
        {suggestions.length > 0 ? (
          <Field
            label="From the GitHub App"
            hint="Repositories the App can already see. Picking one wires up webhooks immediately."
          >
            <Select
              value={suggestions.includes(fullName) ? fullName : ''}
              onChange={setFullName}
              options={suggestions.map((name) => ({ value: name, label: name }))}
              placeholder="Choose a repository"
            />
          </Field>
        ) : null}

        <Field label="Or type it" hint="owner/name, or paste a GitHub URL.">
          <Input
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            placeholder="acme/site"
          />
        </Field>

        <Field label="Default branch" hint="Leave blank for main.">
          <Input
            value={branch}
            onChange={(event) => setBranch(event.target.value)}
            placeholder="main"
          />
        </Field>
      </Dialog>
    </Page>
  );
}
